import { DeleteModal } from 'common-front/src/designSystem/components/deleteModal';
import { Dropdown } from 'common-front/src/designSystem/components/dropdown/dropdown';
import { ItemIcon } from 'common-front/src/designSystem/components/dropdown/item';
import { Menu } from 'common-front/src/designSystem/components/dropdown/menu';
import { Trigger } from 'common-front/src/designSystem/components/dropdown/trigger';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { UserInformationsBarcodeModal } from 'common-front/src/users/modal/userInformationsBarcodeModal';
import { copy } from 'common-front/src/util/commands';
import { Badge } from 'common/src/designSystem/components/badge';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { I } from 'common/src/designSystem/components/i';
import { Link } from 'common/src/designSystem/components/link';
import { UserInformationsEventQuery } from 'common/src/generated/types';
import { DateTimeService } from 'common/src/services/dateTimeService';
import { useService } from 'common/src/util/dependencies/dependencies';
import { LocaleFormats } from 'common/src/util/luxon';
import { isNonEmptyString } from 'common/src/util/string';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useEventContext } from '../../../events/show/eventContext';
import { useVolunteerRegistrationTicketIdRegenerateMutation } from '../../../generated/graphqlHooks';

interface IUserInformationsWeezeventProps {
    nameOrEmail: string;
    volunteerRegistration: UserInformationsEventQuery['event']['volunteerRegistration'];

    reload(): void;
}

export const UserInformationsWeezevent = (props: IUserInformationsWeezeventProps) => {
    const {
        translate,
        params: { eventId }
    } = useHeavent();
    const { isEventAdmin } = useEventContext();
    const dateTimeService = useService(DateTimeService);
    const { mutate: ticketIdRegenerate } = useVolunteerRegistrationTicketIdRegenerateMutation();
    const [isTicketRegenerateOpen, setIsTicketRegenerateOpen] = React.useState(false);
    const lastSynchronizedAt = React.useMemo(() => {
        if (
            isNonEmptyString(props.volunteerRegistration.weezevent.lastSynchronizedAt) &&
            DateTime.fromISO(props.volunteerRegistration.weezevent.lastSynchronizedAt)?.isValid
        ) {
            return dateTimeService.toLocaleString(
                DateTime.fromISO(
                    props.volunteerRegistration.weezevent.lastSynchronizedAt
                ).toLocal(),
                LocaleFormats.DateTime
            );
        } else {
            return '-';
        }
    }, [props.volunteerRegistration]);
    const pairedAt = React.useMemo(() => {
        if (props.volunteerRegistration.weezeventPairedAt?.isValid) {
            return dateTimeService.toLocaleString(
                props.volunteerRegistration.weezeventPairedAt.toLocal(),
                LocaleFormats.DateTime
            );
        } else {
            return '-';
        }
    }, [props.volunteerRegistration]);

    return (
        <>
            <Flex
                css={{
                    background: 'white',
                    bd: true,
                    padding: '$5 $6'
                }}
                direction="column"
                gap="4"
                width={1}
            >
                <Flex align="center" gap="2">
                    <I icon="memo-circle-info" />

                    <Box font="gray900 textMd medium">{translate('informations_22954')}</Box>
                </Flex>

                <Flex align="center" gap="5">
                    <UserInformationsBarcodeModal
                        nameOrEmail={props.nameOrEmail}
                        size={64}
                        ticketId={props.volunteerRegistration.ticketId}
                    />

                    <Flex direction="column" gap="1">
                        <Box color="gray500">{translate('TicketId')}</Box>

                        <Dropdown>
                            <Trigger>
                                <Badge
                                    color="white"
                                    cursor="pointer"
                                    leftIcon="ticket"
                                    rightIcon="chevron-down"
                                    size="md"
                                >
                                    {props.volunteerRegistration.ticketId}
                                </Badge>
                            </Trigger>

                            <Menu placement="bottom-start">
                                <ItemIcon
                                    icon="copy"
                                    onClick={() => {
                                        copy(props.volunteerRegistration.ticketId);
                                    }}
                                >
                                    {translate('copier_75889')}
                                </ItemIcon>

                                {isNonEmptyString(
                                    props.volunteerRegistration.weezeventTicketUrl
                                ) && (
                                    <Link to={props.volunteerRegistration.weezeventTicketUrl}>
                                        <ItemIcon icon="arrow-up-right-from-square">
                                            {translate('ouvrir_dans_wee_97529')}
                                        </ItemIcon>
                                    </Link>
                                )}

                                {isNonEmptyString(props.volunteerRegistration.weezeventPayUrl) && (
                                    <Link to={props.volunteerRegistration.weezeventPayUrl}>
                                        <ItemIcon icon="arrow-up-right-from-square">
                                            {translate('ouvrir_dans_wee_14636')}
                                        </ItemIcon>
                                    </Link>
                                )}

                                {isEventAdmin && (
                                    <ItemIcon
                                        icon="repeat"
                                        onClick={() => setIsTicketRegenerateOpen(true)}
                                    >
                                        {translate('r_g_n_rer_le_co_81294')}
                                    </ItemIcon>
                                )}
                            </Menu>
                        </Dropdown>
                    </Flex>

                    <Flex direction="column" gap="1">
                        <Box color="gray500">{translate('num_ro_de_puce_50039')}</Box>
                        <Box font="gray800 textSm medium">
                            {isNonEmptyString(props.volunteerRegistration.shortTag)
                                ? props.volunteerRegistration.shortTag
                                : '-'}
                        </Box>
                    </Flex>

                    <Flex direction="column" gap="1">
                        <Box color="gray500">{translate('derni_re_synchr_59823')}</Box>
                        <Box font="gray800 textSm medium">{lastSynchronizedAt}</Box>
                    </Flex>

                    <Flex direction="column" gap="1">
                        <Box color="gray500">{translate('appair_le_70213')}</Box>
                        <Box font="gray800 textSm medium">{pairedAt}</Box>
                    </Flex>
                </Flex>
            </Flex>

            {isTicketRegenerateOpen && (
                <DeleteModal
                    buttonText={translate('r_g_n_rer_37440')}
                    subtext={[
                        translate('_tes_vous_s_r_d_57752'),
                        translate('cela_rendra_l_a_42228')
                    ]}
                    text={translate('r_g_n_rer_le_co_81294')}
                    onClose={() => setIsTicketRegenerateOpen(false)}
                    onDelete={async () => {
                        await ticketIdRegenerate({
                            eventId,
                            volunteerRegistrationId: props.volunteerRegistration.id
                        });

                        props.reload();
                    }}
                />
            )}
        </>
    );
};
